import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Play, Pause, Check, Loader2, ArrowRight } from "lucide-react";
import { MinimalShell } from "../components/layout/MinimalShell";
import { GRACEFUL } from "../data/siteContent";
import { TRACKS } from "../data/tracks";
import { useAudio } from "../context/AudioContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GracefulPassages() {
  const { playTrack, currentId, isPlaying } = useAudio();
  const previews = GRACEFUL.previewIds.map((id) => TRACKS.find((t) => t.id === id)).filter(Boolean);

  const [bumps, setBumps] = useState({});
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const toggleBump = (id) => setBumps((b) => ({ ...b, [id]: !b[id] }));
  const selected = GRACEFUL.bumps.filter((b) => bumps[b.id]);
  const bumpTotal = selected.reduce((s, b) => s + b.price, 0);

  const reserve = async (e) => {
    e.preventDefault();
    if (!name.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { setError("Please enter your name and a valid email."); return; }
    setError(""); setLoading(true);
    const interest = selected.length ? `order-bumps: ${selected.map((b) => `${b.title} ($${b.price})`).join(", ")}` : "graceful-passages-access";
    try {
      await axios.post(`${API}/lead`, { name, email, source: GRACEFUL.source, avatar: "families", interest });
      setDone(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <MinimalShell>
      {/* Hero + previews */}
      <section data-testid="graceful-page" className="relative pt-32 md:pt-40 pb-16 overflow-hidden grain">
        <div className="aurora-weave" />
        <div className="line-weave" />
        <div className="relative z-10 max-w-5xl mx-auto px-5 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-sm tracking-[0.24em] uppercase text-[#E7CC86]/80 mb-4">{GRACEFUL.eyebrow}</p>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-cream mb-5">
              For life's most <span className="text-gold">important moments.</span>
            </h1>
            <p className="text-lg text-cream/70">{GRACEFUL.sub}</p>
          </div>

          <p className="text-center text-xs tracking-[0.24em] uppercase text-cream/45 mb-4">Listen — the first passages, free</p>
          <div className="grid sm:grid-cols-3 gap-3 max-w-3xl mx-auto">
            {previews.map((t) => {
              const active = currentId === t.id;
              const playingThis = active && isPlaying;
              return (
                <button key={t.id} data-testid={`graceful-preview-${t.id}`} onClick={() => playTrack(t.id)}
                  className={`text-left glass rounded-2xl p-4 group ${active ? "border-[#C9A24B]/55" : "hover:border-[#C9A24B]/35"}`}>
                  <div className="relative w-full aspect-square rounded-xl overflow-hidden bg-gradient-to-br from-[#12203a] to-[#05070C] mb-3">
                    <img loading="lazy" src={t.cover} alt="" className="absolute inset-0 w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
                      {playingThis ? <Pause size={26} className="text-[#E7CC86]" fill="#E7CC86" /> : <Play size={26} className="ml-1 text-[#E7CC86]" fill="#E7CC86" />}
                    </div>
                  </div>
                  <p className={`font-display text-base truncate ${active ? "text-[#E7CC86]" : "text-cream"}`}>{t.title}</p>
                  <p className="text-xs text-cream/50 truncate">{t.feat}</p>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Order bumps + reserve */}
      <section className="relative py-16 md:py-24 bg-[#0B1020] overflow-hidden">
        <div className="line-weave" />
        <div className="relative z-10 max-w-2xl mx-auto px-5 md:px-8">
          <div className="glass rounded-3xl p-8 md:p-10">
            {!done ? (
              <form onSubmit={reserve} data-testid="graceful-reserve-form">
                <h2 className="font-display text-2xl md:text-3xl text-cream mb-2">Reserve your copy</h2>
                <p className="text-cream/60 mb-7">Get first access when it opens — and add these companions to go deeper.</p>

                <div className="space-y-3 mb-6">
                  {GRACEFUL.bumps.map((b) => {
                    const on = !!bumps[b.id];
                    return (
                      <button type="button" key={b.id} data-testid={`bump-${b.id}`} onClick={() => toggleBump(b.id)}
                        className={`w-full text-left rounded-2xl p-5 border flex items-start gap-4 transition-all ${on ? "border-[#C9A24B] bg-[#C9A24B]/8" : "border-[#C9A24B]/25 hover:border-[#C9A24B]/50"}`}>
                        <span className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 mt-0.5 ${on ? "btn-gold" : "border border-[#C9A24B]/40"}`}>
                          {on && <Check size={15} className="text-[#14100A]" />}
                        </span>
                        <span className="flex-1">
                          <span className="flex items-baseline justify-between gap-3">
                            <span className="font-display text-lg text-cream">{b.title}</span>
                            <span className="text-[#E7CC86] font-semibold whitespace-nowrap">+${b.price}</span>
                          </span>
                          <span className="block text-cream/60 text-sm mt-1 leading-snug">{b.note}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div className="space-y-3 mb-5">
                  <label htmlFor="graceful-name" className="sr-only">Your name</label>
                  <input id="graceful-name" data-testid="graceful-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" autoComplete="name"
                    className="w-full px-5 py-4 rounded-2xl bg-white/5 border border-[#C9A24B]/25 text-cream text-lg outline-none focus:border-[#C9A24B] transition-colors" />
                  <label htmlFor="graceful-email" className="sr-only">Your email address</label>
                  <input id="graceful-email" data-testid="graceful-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email" autoComplete="email"
                    className="w-full px-5 py-4 rounded-2xl bg-white/5 border border-[#C9A24B]/25 text-cream text-lg outline-none focus:border-[#C9A24B] transition-colors" />
                </div>

                {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
                <button data-testid="graceful-reserve-submit" type="submit" disabled={loading}
                  className="btn-gold w-full py-4 rounded-2xl font-semibold text-lg flex items-center justify-center gap-2 disabled:opacity-70">
                  {loading ? <><Loader2 size={20} className="animate-spin" /> Reserving…</>
                    : <>{selected.length ? `Reserve + Add $${bumpTotal} in add-on${selected.length > 1 ? "s" : ""}` : "Reserve My Copy"} <ArrowRight size={20} /></>}
                </button>
                <p className="text-xs text-cream/35 mt-3 leading-relaxed">No charge today — you're reserving your place. We'll email you when it opens, with your selected companions noted.</p>
              </form>
            ) : (
              <div data-testid="graceful-reserved" className="text-center py-6">
                <div className="w-14 h-14 rounded-full btn-gold flex items-center justify-center mx-auto mb-4"><Check size={26} className="text-[#14100A]" /></div>
                <h2 className="font-display text-2xl text-cream mb-2">Your place is reserved.</h2>
                <p className="text-cream/60">{selected.length ? "We've noted your companion selections. " : ""}Watch your inbox — we'll be in touch as this opens.</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </MinimalShell>
  );
}
